import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Col, Input, message, Modal, PageHeader, Row, Table, Upload,} from "antd";
import {DownloadOutlined, ReloadOutlined, SaveOutlined, SearchOutlined, UploadOutlined,} from "@ant-design/icons";
import _ from "lodash";

import {downloadEmployeesBulkTemplate, getEmployeesBulk, submitEmployeesBulk} from "../api/employeesBulk";
import {getPaginationConfig} from "../util/showAllPagination";
import {useDynamicTableHeight} from "../util/useDynamicTableHeight";
import EmployeeBulkFullInfoModal from "./EmployeeBulkFullInfoModal";
import {api_path, getAuthHeader} from "../api";
import sorters from "../util/sorters";
import {getPracticeAbbrev} from "../util/practiceAbbrev";

const EmployeesBulk = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);
  const submitDisabled = employees.length === 0 || employees.some((employee) => employee.status === "Error" || employee.status === "Submitted");
  const [submitModalOpen, setSubmitModalOpen] = useState(false);

  const columns = [
    {
      title: "Effective Date",
      dataIndex: "effectiveDate",
      ellipsis: true,
      width: 100,
      sorter: (a, b) => sorters.date(a.effectiveDate, b.effectiveDate),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => sorters.string(a.name, b.name)
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => sorters.string(a.email, b.email)
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: (a, b) => sorters.string(a.title, b.title),
    },
    {
      title: "Level",
      dataIndex: "level",
      ellipsis: true,
      width: 60,
      align: "right",
      sorter: (a, b) => (a.level || 0) - (b.level || 0),
    },
    {
      title: "Practice",
      dataIndex: "practiceName",
      width: 60,
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.practiceName, b.practiceName),
      render: (practiceName) => getPracticeAbbrev(practiceName),
    },
    {
      title: "Capability",
      dataIndex: "capability",
      sorter: (a, b) => sorters.string(a.capability, b.capability)
    },
    {
      title: "Location",
      dataIndex: "locationName",
      sorter: (a, b) => sorters.string(a.locationName, b.locationName)
    },
    {
      title: "PTO Policy",
      dataIndex: "ptoPolicy",
      sorter: (a, b) => sorters.string(a.ptoPolicy, b.ptoPolicy)
    },
    {
      title: "Start Date",
      width: 100,
      ellipsis: true,
      dataIndex: "startDate",
      sorter: (a, b) => sorters.date(a.startDate, b.startDate)
    },
    {
      title: "Term Date",
      width: 100,
      ellipsis: true,
      dataIndex: "termDate",
      sorter: (a, b) => sorters.date(a.termDate, b.termDate)
    },
    {
      title: "Employment Status",
      dataIndex: "employment_status",
      sorter: (a, b) => sorters.string(a.employmentStatus, b.employmentStatus)
    },
    {
      title: "Status",
      dataIndex: "status",
      ellipsis: true,
      sorter: (a, b) => sorters.string(a.status, b.status),
      width: 80,
    },
  ];
  const loadBulkEmployees = useCallback(async () => {
    try {
      setLoading(true);

      const employeesData = await getEmployeesBulk();

      const employees = employeesData.map((e) => ({
        id: e.a,
        effectiveDate: e.b,
        email: e.c,
        name: e.d,
        title: e.e,
        level: e.f,
        practiceName: e.g,
        locationName: e.h,
        capability: e.i,
        manager: e.j,
        careerCoach: e.k,
        startDate: e.l,
        termDate: e.m,
        employmentStatus: e.n,
        ptoPolicy: e.o,
        ptoModifier: e.p,
        ptoHoursOverride: e.q,
        contractorRate: e.r,
        status: e.s,
        errors: e.t,
        psaInstances: e.u,
        primaryPsaInstance: e.v
      }));

      setEmployees(employees);

      filterEmployees(employees, searchText);
    } catch (e) {
      console.error("Could not get employees");
    } finally {
      setLoading(false);
    }
  }, [searchText]);
  const filterEmployees = (employeesData, text) => {
    setFilteredEmployees(
      _.chain(employeesData)
        .filter(
          (employee) =>
            text === "" ||
            _.includes(
              _.toLower(employee.email || ""),
              _.toLower(text)
            ) ||
            _.includes(
              _.toLower(employee.fullName || ""),
              _.toLower(text)
            )
        )
        .value()
    );
  };

  const uploadBulkCSVProps = (onChange) => {
    return {
      name: 'employees_bulk_csv',
      accept: '.csv',
      action: api_path('/upload_employees_bulk'),
      withCredentials: true,
      headers: {
        "Authorization": getAuthHeader(),
        "X-Requested-With": null,
      },
      onChange: onChange,
      showUploadList: false
    }
  }

  const uploadProps = uploadBulkCSVProps((info) => {
    switch (info.file.status) {
      case 'done':
        message.success(`${info.file.name} file uploaded successfully`);
        break;
      case 'error':
        if (info.file.error.status === 400) {
          message.error(`${info.file.name} file upload failed: ${info.file.response}`, 5)
        } else {
          message.error(`${info.file.name} file upload failed.`);
        }
        break;
      case 'uploading':
        // ignore
        break;
      default:
        message.error(`${info.file.name} has incorrect status ${info.file.status}`);
    }
    loadBulkEmployees()
  });

  const search = (text) => {
    setSearchText(text);
    filterEmployees(employees, text);
  };


  const handleRowClick = (record) => {
    setSelectedEmployee(record);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setSelectedEmployee(null);
    setIsModalVisible(false);
  };

  const downloadTemplate = async () => {
    try {
      const {data: base64Data} = await downloadEmployeesBulkTemplate();
      const blob = await fetch(`data:text/csv;base64,${base64Data}`).then((r) => r.blob());

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "employees_bulk_upload.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download template:", error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await submitEmployeesBulk();
      message.success("Bulk employees submitted successfully");
      await loadBulkEmployees();
    } catch (error) {
      console.error("Failed to submit bulk employees:", error);
      message.error("Failed to submit bulk employees");
    } finally {
      setLoading(false);
      setSubmitModalOpen(false);
    }
  };

  useEffect(() => {
    loadBulkEmployees();
  }, [loadBulkEmployees]);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Employee Bulk Upload"
            subTitle={`(${filteredEmployees.length} records)`}
            style={{ padding: 0 }}
          />
        </Col>
      </Row>

      <Row style={{ marginBottom: 10 }}>
        <Col span={6} style={{ marginRight: 10 }}>
          <Input
            placeholder="Search by name or email"
            prefix={<SearchOutlined />}
            onChange={(e) => search(e.target.value)}
            allowClear
          />
        </Col>

        <Col flex="auto">
          <Button
            style={{ float: "right" }}
            icon={<ReloadOutlined />}
            onClick={loadBulkEmployees}
            loading={loading}
          >
            Refresh
          </Button>


          <Button
            style={{ float: "right", marginRight: 10 }}
            icon={<DownloadOutlined />}
            disabled={loading}
            onClick={downloadTemplate}
          >
            Download Template
          </Button>
          <Col style={{ float: "right", marginRight: 10}}>
            <Upload {...uploadProps}>
              <Button
                icon={<UploadOutlined/>}
                disabled={loading}
              >
                Upload Bulk Employees
              </Button>

            </Upload>
          </Col>
          <Col style={{ float: "right", marginRight: 10}}>
              <Button
                icon={<SaveOutlined/>}
                disabled={submitDisabled || loading}
                loading={loading}
                onClick= {()=>setSubmitModalOpen(true)}
              >
                Submit Bulk Employees
              </Button>
          </Col>
        </Col>
      </Row>
      <Table
        ref={tableContainerRef}
        rowKey="id"
        dataSource={filteredEmployees}
        columns={columns}
        pagination={getPaginationConfig(filteredEmployees.length)}
        size="small"
        loading={loading}
        scroll={{x: "100%", y: tableHeight}}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
      />
      {selectedEmployee && (
        <EmployeeBulkFullInfoModal
          visible={isModalVisible}
          onClose={closeModal}
          employee={selectedEmployee}
        />
      )}
  <Modal
    title="Confirm Bulk Employees Submit"
    open={submitModalOpen}
    onCancel={() => setSubmitModalOpen(false)}
    onOk={handleSubmit}
    okText="Submit"
    >
    <p>This will move all the data into Effective dating. Are you sure?
    </p>
  </Modal>
    </>
  );
};

export { EmployeesBulk };